/**
 * Notifications hooks (notifications part two) — thin TanStack Query
 * layer over notificationsApi / notificationPreferencesApi /
 * adminNotificationDefaultsApi (src/lib/api.ts), mirroring the
 * announcements.ts / workNotifications.ts hook convention.
 *
 * Server is authoritative on every read (unread_count, the effective
 * preference resolution) and every write; these hooks carry no
 * authorization or resolution logic of their own.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  notificationPreferencesApi,
  adminNotificationDefaultsApi,
  type NotificationWire,
  type NotificationRule,
  type NotificationPreferencesView,
  type TenantNotificationDefaultRow,
} from '@/lib/api';

/**
 * Where a notification opens. notify() stores each link already carrying
 * the tenant (api/_shared/notifications/templates.ts builds every one as
 * `/t/<slug>/...`), so a link that begins with `/t/` is opened as stored;
 * prefixing it again landed on `/t/<slug>/t/<slug>/...`, a Not found page.
 * A link without the prefix is opened under the tenant.
 */
export function notificationHref(tenantSlug: string, link: string): string {
  return link.startsWith('/t/') ? link : `/t/${tenantSlug}${link}`;
}

const UNREAD_COUNT_POLL_MS = 60_000;

export function notificationsUnreadCountQueryKey(slug: string): readonly unknown[] {
  return ['notifications', slug, 'unread-count'];
}
export function notificationsListQueryKey(slug: string, opts: { unreadOnly?: boolean }): readonly unknown[] {
  return ['notifications', slug, 'list', opts.unreadOnly ?? false];
}
export function notificationPreferencesQueryKey(slug: string): readonly unknown[] {
  return ['notification-preferences', slug];
}
export function adminNotificationDefaultsQueryKey(slug: string): readonly unknown[] {
  return ['admin-notification-defaults', slug];
}

/**
 * The bell's number: polled every 60s and refreshed on window focus.
 * Pass `enabled: false` above a capability gate the same way
 * useAnnouncements/useWorkNotifications do.
 */
export function useNotificationsUnreadCount(
  slug: string,
  opts: { enabled?: boolean } = {},
): { unreadCount: number; isLoading: boolean } {
  const query = useQuery({
    queryKey: notificationsUnreadCountQueryKey(slug),
    queryFn: () => notificationsApi.unreadCount(slug),
    staleTime: UNREAD_COUNT_POLL_MS,
    refetchInterval: UNREAD_COUNT_POLL_MS,
    refetchOnWindowFocus: true,
    enabled: opts.enabled ?? true,
  });
  return { unreadCount: query.data?.unread_count ?? 0, isLoading: query.isLoading };
}

export interface UseNotificationsListResult {
  rows: readonly NotificationWire[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  /** The failed read's error, for the banner that describes it. */
  error: unknown;
}

/** The bell panel's latest rows, or the inbox page's filtered list. */
export function useNotificationsList(
  slug: string,
  opts: { unreadOnly?: boolean; limit?: number; offset?: number; enabled?: boolean } = {},
): UseNotificationsListResult {
  const query = useQuery({
    queryKey: [...notificationsListQueryKey(slug, opts), opts.limit, opts.offset],
    queryFn: () =>
      notificationsApi.list(slug, {
        unreadOnly: opts.unreadOnly,
        limit: opts.limit,
        offset: opts.offset,
      }),
    enabled: opts.enabled ?? true,
  });
  return {
    rows: query.data?.rows ?? [],
    unreadCount: query.data?.unread_count ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/** Mark one notification read; invalidates the count + every list. */
export function useMarkNotificationRead(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(slug, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', slug] });
    },
  });
}

/** Mark every unread row read; invalidates the count + every list. */
export function useMarkAllNotificationsRead(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', slug] });
    },
  });
}

/** The signed-in person's own preferences screen. */
export function useNotificationPreferences(
  slug: string,
): { view: NotificationPreferencesView | undefined; isLoading: boolean; isError: boolean; error: unknown } {
  const query = useQuery({
    queryKey: notificationPreferencesQueryKey(slug),
    queryFn: () => notificationPreferencesApi.list(slug),
  });
  return { view: query.data, isLoading: query.isLoading, isError: query.isError, error: query.error };
}

export function useSetNotificationPreference(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventKey, rule }: { eventKey: string; rule: NotificationRule }) =>
      notificationPreferencesApi.set(slug, eventKey, rule),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationPreferencesQueryKey(slug) });
    },
  });
}

export function useResetNotificationPreference(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventKey: string) => notificationPreferencesApi.reset(slug, eventKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationPreferencesQueryKey(slug) });
    },
  });
}

/** The owner-facing Admin > Notifications tenant defaults grid. */
export function useAdminNotificationDefaults(
  slug: string,
): { rows: readonly TenantNotificationDefaultRow[]; isLoading: boolean; isError: boolean; error: unknown } {
  const query = useQuery({
    queryKey: adminNotificationDefaultsQueryKey(slug),
    queryFn: () => adminNotificationDefaultsApi.list(slug),
  });
  return {
    rows: query.data?.defaults ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

export function useSetAdminNotificationDefaults(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaults: Record<string, NotificationRule>) =>
      adminNotificationDefaultsApi.set(slug, defaults),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminNotificationDefaultsQueryKey(slug) });
    },
  });
}

/** Re-poll the unread count immediately when the window regains focus,
 *  on top of TanStack's own refetchOnWindowFocus (belt + suspenders for
 *  environments where the query's focus manager isn't wired, e.g. some
 *  test harnesses) — a no-op query-key invalidation is cheap. */
export function useRefetchUnreadCountOnFocus(slug: string): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    function onFocus(): void {
      void queryClient.invalidateQueries({ queryKey: notificationsUnreadCountQueryKey(slug) });
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [slug, queryClient]);
}
